import { useEffect, useState } from "react";
import { Bell, Mail, CheckSquare, Calendar, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { default as NotificationView } from "./NotificationView";
import { EmailView } from "./EmailView";
import type { ComposeEmailData } from "@/lib/messagesService";
import { TasksView } from "./TasksView";
import { CalendarView } from "./CalendarView";

interface SlideoutPanelProps {
  isOpen: boolean;
  onClose: () => void;
  type: "notifications" | "messages" | "tickets" | "calendar";
  scheduleMeetingData?: any;
  initialComposeData?: ComposeEmailData | null;
}

export function SlideoutPanel({
  isOpen,
  onClose,
  type,
  scheduleMeetingData,
  initialComposeData,
}: SlideoutPanelProps) {
  // The panel is only mounted while open, so it would otherwise pop in at its
  // final position. Start off-screen and slide in on the next frame.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  const renderContent = () => {
    switch (type) {
      case "notifications":
        return <NotificationView />;
      case "messages":
        return (
          <EmailView
            onClose={onClose}
            initialComposeData={initialComposeData || undefined}
          />
        );
      case "tickets":
        return <TasksView onClose={onClose} />;
      case "calendar":
        return <CalendarView scheduleMeetingData={scheduleMeetingData} />;
      default:
        return null;
    }
  };

  const getIcon = () => {
    switch (type) {
      case "notifications":
        return <Bell className="h-5 w-5" />;
      case "messages":
        return <Mail className="h-5 w-5" />;
      case "tickets":
        return <CheckSquare className="h-5 w-5" />;
      case "calendar":
        return <Calendar className="h-5 w-5" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    if (type === "tickets") return "Tasks";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getFullPageUrl = (): string | null => {
    switch (type) {
      case "messages":
        return "/emails";
      case "tickets":
        return "/tasks";
      case "calendar":
        return "/calendar";
      case "notifications":
        return null; // No dedicated full page for notifications
      default:
        return null;
    }
  };

  const handleViewFullPage = () => {
    const url = getFullPageUrl();
    if (url) {
      // Use direct navigation for reliable full page load
      window.location.href = url;
    }
  };

  const fullPageUrl = getFullPageUrl();

  // Messages type uses EmailView which has its own header structure
  if (type === "messages") {
    return (
      <div
        className={
          "fixed right-0 bg-white dark:bg-gray-900 z-40 transition-transform duration-300 ease-in-out overflow-hidden w-[800px] shadow-xl " +
          (isOpen && entered ? "translate-x-0" : "translate-x-full")
        }
        style={{
          // Sit exactly below the fixed header (which is offset by an optional
          // announcement banner). z-40 keeps the panel under the header (z-50)
          // so it never overlaps the header or its bottom border.
          top: "calc(var(--banner-offset, 0px) + 4rem)",
          height: "calc(100vh - var(--banner-offset, 0px) - 4rem)",
          boxShadow: isOpen
            ? "-8px 0 24px -4px rgba(0, 0, 0, 0.1), -4px 0 8px -2px rgba(0, 0, 0, 0.06)"
            : "none",
        }}
      >
        <EmailView
          onClose={onClose}
          initialComposeData={initialComposeData || undefined}
        />
      </div>
    );
  }

  return (
    <>
      {/* Panel */}
      <div
        className={
          "fixed right-0 bg-white dark:bg-gray-900 z-40 transition-transform duration-300 ease-in-out overflow-hidden w-[800px] shadow-xl " +
          (isOpen && entered ? "translate-x-0" : "translate-x-full")
        }
        style={{
          // Sit exactly below the fixed header (which is offset by an optional
          // announcement banner). z-40 keeps the panel under the header (z-50)
          // so it never overlaps the header or its bottom border.
          top: "calc(var(--banner-offset, 0px) + 4rem)",
          height: "calc(100vh - var(--banner-offset, 0px) - 4rem)",
          boxShadow: isOpen
            ? "-8px 0 24px -4px rgba(0, 0, 0, 0.1), -4px 0 8px -2px rgba(0, 0, 0, 0.06)"
            : "none",
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-background flex-shrink-0">
            <div className="flex items-center space-x-2">
              <div className="text-[#1737A2] dark:text-blue-400">{getIcon()}</div>
              <h2 className="text-xl font-bold text-foreground">{getTitle()}</h2>
            </div>
            <div className="flex items-center gap-1">
              {fullPageUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleViewFullPage}
                  className="text-muted-foreground hover:text-accent-foreground"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Full Page
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 bg-background">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
}
